import { Injectable } from "@angular/core";
import {
    IFareComponent,
    IPrice,
    IPriceDetail,
    IProperty,
    ISectorAir,
    ISectorStay,
    ITaxBreakdown,
    ITaxPriceDetail,
    IXId,
    IXRole
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { IAirLine, ICellValue, IHotelLine, ILinesValues, IOtherLine, ITaxConfig, ITotalLine, PriceLineType, TPriceLine } from '../ITablePrice';
import { DEFAULT_SECTOR_KEY, KeysMapperService, TOTAL_KEY } from './keys-mapper.service';
import { ApiPricingRate, ApiPricingXRank, ApiPricingXState, ApiPricingXType } from 'libraries/service/resources/booking/interfaces/ApiSearchParameters';
import { BookingService } from 'libraries/service/booking/booking.service';
import { Yii } from 'libraries/interfaces/Yii';
import { SectorXType } from 'libraries/service/resources/model/search/air/IAirSector';
import { EXRateType } from "libraries/interfaces/ITotal";

const DEFAULT_TAXES_GROUP: string = 'default-tax';

@Injectable()
export class PriceMapperService {
    public constructor(
        private readonly keysMapperService: KeysMapperService,
        private readonly bookingService: BookingService,
    ) {
    }

    public extractSectorsLines(prices: IPrice[], sectors: Array<ISectorStay | ISectorAir>, properties: IProperty[]): TPriceLine[] {
        const linesByKey: Map<string, TPriceLine> = new Map();
        const sectorsById: Map<string, ISectorStay | ISectorAir> = new Map(
            (sectors || []).map((sector: ISectorStay | ISectorAir): [string, ISectorStay | ISectorAir] => [sector.xId, sector])
        );

        const costs: IPrice[] = getCosts(prices);

        for (const cost of costs) {
            if (!cost.detail) {
                continue;
            }

            const details: IPriceDetail[] = [
                ...(cost.detail.base || []),
                ...(cost.detail.fee || []),
                ...(cost.detail.tax || []),
            ];

            for (const detail of details) {
                const lineKey: string = this.keysMapperService.getKey(detail.sector, DEFAULT_SECTOR_KEY);
                const detailSectors: Array<ISectorStay | ISectorAir>
                    = (detail.sector || []).map((sector: ISectorStay | ISectorAir) => sectorsById.get(sector.xId));

                const isAir: boolean = detailSectors.some((sector: ISectorStay | ISectorAir) => sector.xType === SectorXType.air);
                const isHotel: boolean = detailSectors.some((sector: ISectorStay | ISectorAir) => sector.xType === SectorXType.stay);
                // TODO add mapper price for vehicleRental
                if (!linesByKey.has(lineKey)) {
                    if (isAir) {
                        const line: IAirLine = {
                            key: lineKey,
                            type: PriceLineType.air,
                            sectors: detailSectors as ISectorAir[],
                        };

                        linesByKey.set(lineKey, line);
                    } else if (isHotel) {
                        const sector: ISectorStay = detailSectors[0] as ISectorStay;

                        const line: IHotelLine = {
                            key: lineKey,
                            type: PriceLineType.stay,
                            hotel: sector,
                            property: properties && properties.find((property: IProperty) => property.xId === sector.property.xId),
                        };

                        linesByKey.set(lineKey, line);
                    } else {
                        const line: IOtherLine = {
                            key: lineKey,
                            type: PriceLineType.other,
                            text: Yii.t('wui-shared-js', "Autres prestations"),
                        };

                        linesByKey.set(lineKey, line);
                    }

                }
            }
        }


        return Array.from(linesByKey.values());
    }

    public extractSectorsTotal(prices: IPrice[], fareComponents: IFareComponent[] | undefined, allTravellersIds: string[]): ICellValue[] {
        if (!prices || !prices.length) {
            return [];
        }

        const costPrices: IPrice[] = getCosts(prices);

        const totalByKey: Map<string, ICellValue> = new Map();

        for (const price of costPrices) {
            const details: IPriceDetail[] = Object.entries(price.detail || {})
                .filter(([key, values]: [string, IPriceDetail[]]) => key !== "total")
                .map(([key, values]: [string, IPriceDetail[]]) => values)
                .reduce((list: IPriceDetail[], values: IPriceDetail[]) => [...list, ...(values || [])], []);
            const additionnalData: string[] = getAdditionnalData(price, fareComponents, this.bookingService.pricingRateLabels);
            for (const detail of details) {
                const sectorKey: string = this.keysMapperService.getKey(detail.sector, DEFAULT_SECTOR_KEY);
                addToTotal(sectorKey, detail, totalByKey);
                addToTraveller(sectorKey, detail, additionnalData, totalByKey, allTravellersIds);
            }
        }

        return Array.from(totalByKey.values());
    }

    public extractDetails(
        prices: IPrice[],
        baseName: string,
        allTravellersIds: string[],
        taxConfig: ITaxConfig[],
        xType: EXRateType = EXRateType.cost): ILinesValues {
        const pricesFiltered: IPrice[] = getPriceFromXType(prices, xType);
        const cellsByKey: Map<string, ICellValue> = new Map();
        const linesByKeys: Map<string, TPriceLine> = new Map();
        for (const price of pricesFiltered) {
            if (!price.detail) {
                continue;
            }

            this.addDetails(
                (price.detail.base || []),
                cellsByKey,
                linesByKeys,
                allTravellersIds,
                'base',
                baseName
            );
            this.addDetails(
                (price.detail.fee || []),
                cellsByKey,
                linesByKeys,
                allTravellersIds,
                'fees',
                Yii.t('wui-shared-js', "Frais")
            );

            this.addDetails(
                (price.detail.surcharge || []),
                cellsByKey,
                linesByKeys,
                allTravellersIds,
                'surcharge',
                Yii.t('wui-shared-js', "Surcharge")
            );

            this.addTaxesDetails(
                (price.detail.tax || []),
                cellsByKey,
                linesByKeys,
                allTravellersIds,
                taxConfig
            );

        }

        return {
            values: Array.from(cellsByKey.values()),
            lines: Array.from(linesByKeys.values()),
        };
    }

    public extractTotal(
        type: EXRateType,
        name: string,
        prices: IPrice[],
        allTravellersIds: string[],
        defaultState: ApiPricingXState,
    ): ILinesValues {
        const pricesOfType: IPrice[] = (prices || []).filter((price: IPrice) => price.xType === type);

        if (!pricesOfType.length) {
            return {
                lines: [],
                values: []
            };
        }

        const cellsByKey: Map<string, ICellValue> = new Map();
        const linesByKeys: Map<string, ITotalLine> = new Map();

        for (const price of pricesOfType) {
            const state: ApiPricingXState = price.xState
                || (price.xRank && price.xRank === ApiPricingXRank.external
                    ? ApiPricingXState.Manual
                    : defaultState);

            const lineKey: string = `total-${type.toString()}-${state.toString()}`;

            const details: IPriced[] = price.detail
                ? [
                    ...(price.detail.base || []),
                    ...(price.detail.tax || []),
                    ...(price.detail.surcharge || []),
                    ...(price.detail.fee || []),
                ]
                : Array.isArray(price.total.total)
                    ? price.total.total
                    : [
                        price.total.total,
                    ];

            for (const detail of details) {
                addToTotal(
                    lineKey,
                    detail,
                    cellsByKey
                );
                addToTraveller(
                    lineKey,
                    detail,
                    undefined,
                    cellsByKey,
                    allTravellersIds
                );

                if (!linesByKeys.has(lineKey)) {
                    linesByKeys.set(lineKey, {
                        key: lineKey,
                        text: name,
                        type: PriceLineType.total,
                        ...(
                            state !== ApiPricingXState.Guaranteed
                                ? {
                                    additionalInfos: [this.bookingService.getPricingXStateLabel(state)],
                                }
                                : {}
                        ),
                    });
                }
            }
        }


        return {
            lines: Array.from(linesByKeys.values()),
            values: Array.from(cellsByKey.values()),
        };
    }

    private addDetails(
        details: IPriceDetail[],
        cellsByKey: Map<string, ICellValue>,
        linesByKeys: Map<string, TPriceLine>,
        allTravellersIds: string[],
        lineSuffix: string,
        lineName: string
    ): void {
        for (const detailedBase of details) {
            const sectorKey: string = this.keysMapperService.getKey((detailedBase.sector || []), DEFAULT_SECTOR_KEY);
            const lineKey: string = `${sectorKey}-${lineSuffix}`;
            addToTraveller(
                lineKey,
                detailedBase,
                undefined,
                cellsByKey,
                allTravellersIds,
            );

            addToTotal(
                lineKey,
                detailedBase,
                cellsByKey
            );

            linesByKeys.set(lineKey, {
                type: PriceLineType.detail,
                key: lineKey,
                parentKey: sectorKey,
                text: lineName,
            });
        }
    }

    private addTaxesDetails(
        taxes: ITaxPriceDetail[],
        cellsByKey: Map<string, ICellValue>,
        linesByKeys: Map<string, TPriceLine>,
        allTravellersIds: string[],
        taxConfig: ITaxConfig[],
    ): void {
        for (const detailedTax of taxes) {
            // need for split tax by config see: https://projets2.afidium.com/issues/22170
            if (detailedTax.taxBreakdown && detailedTax.taxBreakdown.length) {
                this.addTaxesBreakdown(
                    detailedTax,
                    cellsByKey,
                    linesByKeys,
                    allTravellersIds,
                    taxConfig
                );

            } else {
                const sectorKey: string = this.keysMapperService.getKey(detailedTax.sector, DEFAULT_SECTOR_KEY);
                const lineKey: string = `${sectorKey}-tax-${DEFAULT_TAXES_GROUP}`;
                addToTraveller(
                    lineKey,
                    detailedTax,
                    undefined,
                    cellsByKey,
                    allTravellersIds
                );

                addToTotal(
                    lineKey,
                    detailedTax,
                    cellsByKey,
                );

                if (!linesByKeys.has(lineKey)) {
                    linesByKeys.set(lineKey, {
                        type: PriceLineType.detail,
                        parentKey: sectorKey,
                        text: Yii.t('wui-shared-js', "Taxes"),
                        key: lineKey,
                    });
                }

            }
        }
    }

    private addTaxesBreakdown(
        detailedTax: ITaxPriceDetail,
        cellsByKey: Map<string, ICellValue>,
        linesByKeys: Map<string, TPriceLine>,
        allTravellersIds: string[],
        taxConfig: ITaxConfig[]
    ): void {
        const sectorKey: string = this.keysMapperService.getKey(detailedTax.sector, DEFAULT_SECTOR_KEY);
        const taxGroups: ITaxGroup[] = getTaxGroups(detailedTax, taxConfig);

        for (const group of taxGroups) {
            const lineKey: string = `${sectorKey}-tax-${group.code}`;
            let amount: number = 0;
            const currency: string = group.breakdowns[0].currency;

            for (const taxBreakdown of group.breakdowns) {
                amount += taxBreakdown.unitAmount * ((detailedTax.traveller || []).length || allTravellersIds.length);
            }

            const taxGroup: IPriced = {
                amount,
                currency,
                traveller: detailedTax.traveller,
            };
            addToTraveller(
                lineKey,
                taxGroup,
                undefined,
                cellsByKey,
                allTravellersIds
            );

            addToTotal(
                lineKey,
                taxGroup,
                cellsByKey,
            );

            linesByKeys.set(lineKey, {
                type: PriceLineType.detail,
                parentKey: sectorKey,
                key: lineKey,
                text: group.name,
            });
        }
    }
}

function getAdditionnalData(
    price: IPrice,
    fareComponents: IFareComponent[] | undefined,
    pricingRateLabels: { [key in ApiPricingRate]: string }): string[] | undefined {
    const rates: Set<string> = (price.rate || [])
        .map((rate: IXRole) => pricingRateLabels[rate.xRole])
        .filter(Boolean)
        .reduce((allRates: Set<string>, rate: string): Set<string> => {
            allRates.add(rate);
            return allRates;
        }, new Set());

    const fares: Set<string> = (fareComponents || [])
        .filter((fareComponent: IFareComponent) => fareComponent.price &&
            fareComponent.price.xId === price.xId)
        .map((fareComponent: IFareComponent) => fareComponent.fareFamily && (fareComponent.fareFamily.name || fareComponent.fareFamily.code))
        .filter(Boolean)
        .reduce((allFares: Set<string>, fare: string): Set<string> => {
            allFares.add(fare);
            return allFares;
        }, new Set());

    const additionnalData: string[] = [...Array.from(rates.values()), ...Array.from(fares.values())];

    return additionnalData.length ? additionnalData : undefined;
}

function addToTotal(
    lineKey: string,
    detail: IPriced,
    cellsByKey: Map<string, ICellValue>,
): void {
    let total: ICellValue = cellsByKey.get(`${lineKey}-${TOTAL_KEY}`);

    if (!total) {
        total = {
            lineKey,
            columnKey: TOTAL_KEY,
            value: {
                value: 0,
                currency: detail.currency,
                isAutomaticallyDistributed: false,
            },
        };

        cellsByKey.set(`${lineKey}-${TOTAL_KEY}`, total);
    }
    total.value.value = Number(total.value.value) + Number(detail.amount || 0);
}

interface IPriced {
    traveller?: IXId[];
    amount: number;
    currency: string;
}

function addToTraveller(
    lineKey: string,
    detail: IPriced,
    additionalInfos: string[] | undefined,
    cellByKey: Map<string, ICellValue>,
    allTravellersIds: string[]
): void {
    let detailTravellers: IXId[];
    // tslint:disable-next-line:prefer-conditional-expression
    if (Array.isArray(detail.traveller)) {
        // multiple travellers
        detailTravellers = detail.traveller;
    } else if(detail.traveller) {
        // single traveller
        detailTravellers =  [detail.traveller];
    } else {
        // no traveller
        detailTravellers = null;
    }

    const travellerIds: string[] = detailTravellers ? detailTravellers.map((element: IXId) => element.xId) : allTravellersIds || [];

    for (const travellerId of travellerIds) {
        const mapKey: string = `${lineKey}-${travellerId}`;
        let cell: ICellValue = cellByKey.get(mapKey);

        if (!cell) {
            cell = {
                lineKey,
                columnKey: travellerId,
                value: {
                    value: 0,
                    currency: detail.currency,
                    ...additionalInfos ? { additionalInfos } : {},
                    isAutomaticallyDistributed: !detailTravellers,
                },
            };

            cellByKey.set(mapKey, cell);
        }

        (cell.value.value as number) += Number(detail.amount || 0) / travellerIds.length;
        cell.value.isAutomaticallyDistributed = cell.value.isAutomaticallyDistributed || !detailTravellers;
    }
}

function getCosts(prices: IPrice[]): IPrice[] {
    return (prices || [])
        .filter((price: IPrice) => price.xType === EXRateType.cost);
}

function getPriceFromXType(prices: IPrice[], xType: EXRateType): IPrice[] {
    return (prices || [])
        .filter((price: IPrice) => price.xType === xType);
}

interface ITaxGroup {
    name: string;
    code: string;
    breakdowns: ITaxBreakdown[];
}

function getTaxGroups(detailedTax: ITaxPriceDetail, taxConfig: ITaxConfig[]): ITaxGroup[] {
    if (!taxConfig || !taxConfig.length) {
        return [{
            name: Yii.t('wui-shared-js', "Taxes"),
            code: DEFAULT_TAXES_GROUP,
            breakdowns: detailedTax.taxBreakdown
        }];
    }

    const groupsByKey: Map<string, ITaxGroup> = new Map();
    const others: ITaxGroup = {
        breakdowns: [],
        code: DEFAULT_TAXES_GROUP,
        name: Yii.t('wui-shared-js', "Taxes"),
    };

    for (const breakdown of detailedTax.taxBreakdown) {
        const config: ITaxConfig = taxConfig.find((groupConfig: ITaxConfig): boolean => groupConfig.taxCodes && groupConfig.taxCodes.includes(breakdown.code));
        let group: ITaxGroup;

        if (!config) {
            group = others;
        } else {
            group = groupsByKey.get(config.key);

            if (!group) {
                group = {
                    breakdowns: [],
                    code: config.key,
                    name: config.text,
                };
            }
            groupsByKey.set(group.code, group);
        }

        group.breakdowns.push(breakdown);
    }

    const groups: ITaxGroup[] = Array.from(groupsByKey.values());

    if (others.breakdowns && others.breakdowns.length > 0) {
        groups.push(others);
    }

    return groups;
}
